import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  useArchiveBoard,
  useDeleteBoard,
  useUpdateBoard,
} from '../../hooks/useBoards'
import { Board } from '../../types/models'
import { Button } from '../common/Button'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { Input } from '../common/Input'
import { Modal } from '../common/Modal'
import { LabelManager } from '../label/LabelManager'
import { BoardMemberManager } from './BoardMemberManager'
import './BoardSettings.css'

export interface BoardSettingsProps {
  board: Board
  isOpen: boolean
  onClose: () => void
}

/**
 * BoardSettings Component
 * Modal for managing board settings
 * Implements Requirements 4.3, 4.4, 4.5
 */
export const BoardSettings: React.FC<BoardSettingsProps> = ({
  board,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'details' | 'members' | 'labels'>(
    'details'
  )
  const [name, setName] = useState(board.name)
  const [description, setDescription] = useState(board.description || '')
  const [errors, setErrors] = useState<{ name?: string }>({})
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const updateBoard = useUpdateBoard()
  const archiveBoard = useArchiveBoard()
  const deleteBoard = useDeleteBoard()

  // Reset form when board changes
  React.useEffect(() => {
    setName(board.name)
    setDescription(board.description || '')
    setErrors({})
  }, [board])

  const validateForm = (): boolean => {
    const newErrors: { name?: string } = {}

    if (!name.trim()) {
      newErrors.name = t('board.boardNameRequired')
    } else if (name.trim().length > 100) {
      newErrors.name = t('board.boardNameTooLong')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await updateBoard.mutateAsync({
        id: board.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
        },
      })
    } catch (error) {
      // Error is handled by the mutation
    }
  }

  const handleArchive = async () => {
    try {
      await archiveBoard.mutateAsync({
        id: board.id,
        archived: !board.archived,
      })
      setShowArchiveConfirm(false)
      onClose()
    } catch (error) {
      // Error is handled by the mutation
    }
  }

  const handleDelete = async () => {
    try {
      await deleteBoard.mutateAsync(board.id)
      setShowDeleteConfirm(false)
      onClose()
      navigate(`/workspaces/${board.workspaceId}`)
    } catch (error) {
      // Error is handled by the mutation
    }
  }

  const handleClose = () => {
    if (
      !updateBoard.isPending &&
      !archiveBoard.isPending &&
      !deleteBoard.isPending
    ) {
      setActiveTab('details')
      setName(board.name)
      setDescription(board.description || '')
      setErrors({})
      onClose()
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('board.boardSettings')}
        size="lg"
      >
        <div className="board-settings">
          <div className="board-settings__tabs">
            <button
              className={`board-settings__tab ${
                activeTab === 'details' ? 'board-settings__tab--active' : ''
              }`}
              onClick={() => setActiveTab('details')}
            >
              {t('board.details')}
            </button>
            <button
              className={`board-settings__tab ${
                activeTab === 'members' ? 'board-settings__tab--active' : ''
              }`}
              onClick={() => setActiveTab('members')}
            >
              {t('common.members')}
            </button>
            <button
              className={`board-settings__tab ${
                activeTab === 'labels' ? 'board-settings__tab--active' : ''
              }`}
              onClick={() => setActiveTab('labels')}
            >
              {t('board.labels')}
            </button>
          </div>

          <div className="board-settings__content">
            {activeTab === 'details' && (
              <form onSubmit={handleSubmit} className="board-settings__form">
                <Input
                  label={t('board.name')}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={errors.name}
                  placeholder={t('board.enterName')}
                  required
                />

                <Input
                  label={t('board.descriptionOptional')}
                  type="textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('board.enterDescription')}
                  rows={3}
                />

                <div className="board-settings__actions">
                  <Button type="submit" loading={updateBoard.isPending}>
                    {t('board.saveChanges')}
                  </Button>
                </div>

                <div className="board-settings__danger-zone">
                  <h3 className="board-settings__danger-title">
                    {t('board.dangerZone')}
                  </h3>

                  <div className="board-settings__danger-action">
                    <div>
                      <h4>
                        {board.archived
                          ? t('board.unarchiveBoard')
                          : t('board.archiveBoard')}
                      </h4>
                      <p>
                        {board.archived
                          ? t('board.unarchiveBoardDescription')
                          : t('board.archiveBoardDescription')}
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => setShowArchiveConfirm(true)}
                    >
                      {board.archived
                        ? t('common.unarchive')
                        : t('common.archive')}
                    </Button>
                  </div>

                  <div className="board-settings__danger-action">
                    <div>
                      <h4>{t('board.deleteBoard')}</h4>
                      <p>{t('board.deleteBoardDescription')}</p>
                    </div>
                    <Button
                      variant="danger"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      {t('board.deleteBoard')}
                    </Button>
                  </div>
                </div>
              </form>
            )}

            {activeTab === 'members' && (
              <BoardMemberManager boardId={board.id} />
            )}

            {activeTab === 'labels' && <LabelManager boardId={board.id} />}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showArchiveConfirm}
        title={
          board.archived ? t('board.unarchiveBoard') : t('board.archiveBoard')
        }
        message={
          board.archived
            ? t('board.unarchiveBoardConfirm')
            : t('board.archiveBoardConfirm')
        }
        confirmText={
          board.archived ? t('common.unarchive') : t('common.archive')
        }
        onConfirm={handleArchive}
        onCancel={() => setShowArchiveConfirm(false)}
        loading={archiveBoard.isPending}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('board.deleteBoard')}
        message={t('board.deleteBoardConfirmLong')}
        confirmText={t('board.deleteBoard')}
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={deleteBoard.isPending}
      />
    </>
  )
}
